/* eslint-disable react-hooks/exhaustive-deps */
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { Formik } from "formik";
import { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";
import { Button, Offcanvas } from "react-bootstrap";
import * as Yup from "yup";

import { CGMutualFundForm, CGMutualFundsDetailsUploadForm } from "..";
import { IMutualFundForm, IMutualFundFormTypes } from "../../../../config/interfaces";
import {
	MutualFundFormInitValues,
	MutualFundFormSchema
} from "../../../../pages/itr-filing/efiling.config";
import { useAppSelector } from "../../../../redux/Store";
import { ITRFilingSelector } from "../../../../redux/slice/ITRFilingSlice";
import FASvgIcon from "../../../FASvgIcon";
import ScrollToFirstError from "../../../ScrollToFirstError";

type Props = {
	showCanvas: boolean;
	formValues: IMutualFundForm | null;
	formType: IMutualFundFormTypes;
	handleCloseBtnPress: (MutualFundData: IMutualFundForm | null) => void;
};

const CGMutualFundDetailsForm: React.FC<Props> = (props: Props) => {
	const { showCanvas, handleCloseBtnPress, formValues, formType } = props;

	const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

	const [initFormType, setInitFormType] = useState<IMutualFundFormTypes>(null);
	const [initFormValues, setInitFormValues] = useState<IMutualFundForm>(MutualFundFormInitValues);
	const [showForm, setShowForm] = useState<boolean>(false);

	useEffect(() => {
		if (showCanvas && formValues) {
			setInitFormValues(() => cloneDeep(formValues));
		} else {
			setInitFormValues(() => cloneDeep(MutualFundFormInitValues));
		}

		if (showCanvas) {
			setInitFormType(() => formType);
		}
	}, [showCanvas, formValues]);

	useEffect(() => {
		if (initFormType === "MANUAL") {
			setShowForm(() => true);
		} else {
			setShowForm(() => false);
		}
	}, [initFormType]);

	return (
		<>
			<Offcanvas show={showCanvas} placement="end" backdrop={true}>
				<Offcanvas.Header>
					<Offcanvas.Title>
						{initFormType === "MANUAL"
							? "Add/Edit Mutual Fund Details"
							: "Upload Mutual Funds Details to Auto-Fill Your Data"}
					</Offcanvas.Title>
					<Button
						variant="primary"
						className="icon-only hide-offcanvas"
						onClick={() => {
							handleCloseBtnPress(null);
							setTimeout(() => {
								setInitFormType(() => null);
							}, 300);
						}}
					>
						<FASvgIcon icon={faXmark} />
					</Button>
				</Offcanvas.Header>

				{initFormType === "MANUAL" && showForm ? (
					<Formik
						enableReinitialize={true}
						initialValues={initFormValues}
						validationSchema={Yup.object().shape({ ...MutualFundFormSchema })}
						onSubmit={async (values) => {
							let _values = { ...values };
							handleCloseBtnPress(_values);
						}}
					>
						{({ handleSubmit, submitCount, isValid, errors, validateForm }) => (
							<>
								<ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />

								<Offcanvas.Body>
									<CGMutualFundForm />
								</Offcanvas.Body>

								<div className="offcanvas-btns">
									{!isFilingReadOnly ? (
										<Button
											variant="outline-white"
											size="sm"
											onClick={async (event) => {
												event.preventDefault();
												await validateForm();
												handleSubmit();
											}}
										>
											Save and Close
										</Button>
									) : (
										<Button
											variant="outline-white"
											size="sm"
											onClick={() => handleCloseBtnPress(null)}
										>
											Close
										</Button>
									)}
								</div>
							</>
						)}
					</Formik>
				) : (
					<Offcanvas.Body>
						<CGMutualFundsDetailsUploadForm
							handleExtractionSuccess={(responseData) => {
								const newFormValues = {
									...cloneDeep(MutualFundFormInitValues),
									...cloneDeep(responseData)
								};
								setInitFormValues(() => cloneDeep(newFormValues));
								setInitFormType(() => "MANUAL");
								setShowForm(() => true);
							}}
							handleAddManually={() => {
								setInitFormType(() => "MANUAL");
								setShowForm(() => true);
							}}
						/>
					</Offcanvas.Body>
				)}
			</Offcanvas>
		</>
	);
};

export default CGMutualFundDetailsForm;
